import { useRecoilState } from 'recoil'

import {
  findExistingBookings,
  myBookings,
  refreshBookingsForDate,
  sessionState
} from '../state'
import { formatDateToString } from '../utils/FormatHelpers'

/**
 * useHome - Custom hook for managing home page calendar logic
 *
 * Handles date selection, period changes, and booking data management
 * for the home page calendar view.
 */
export function useHome() {
  const [userState, setUserState] = useRecoilState(sessionState)

  const handleFromDayChange = async (e: any) => {
    if (!e.currentTarget) return
    const dt = new Date(e.currentTarget.value)

    setUserState(old => ({
      ...old,
      selectedFromDate: formatDateToString(dt),
      highlightedDates: [] // Clear highlights when date changes
    }))

    refreshBookingsForDate(setUserState, userState, dt)
  }

  const setNewAmountValue = async (num: number) => {
    if (Number.isNaN(num) || num < 1) return

    const dateRangeBookings = await findExistingBookings(
      new Date(userState.selectedFromDate),
      num,
      userState.selectedRecurUnit,
      userState.offices ?? []
    )

    if (dateRangeBookings.length === 0) {
      setUserState(old => ({
        ...old,
        selectedRecurAmount: num,
        highlightedDates: [] // Clear highlights when amount changes
      }))
    } else {
      setUserState(old => ({
        ...old,
        selectedRecurAmount: num,
        bookings: dateRangeBookings,
        highlightedDates: [] // Clear highlights when amount changes
      }))
    }
  }

  const refreshBookings = async () => {
    if (!userState.user?.uid) return

    const dateRangeBookings = await findExistingBookings(
      new Date(userState.selectedFromDate),
      userState.selectedRecurAmount,
      userState.selectedRecurUnit,
      userState.offices ?? []
    )

    // Also refresh user bookings for the My Bookings tab
    const allUserBookings = await myBookings(userState.user.uid)

    setUserState(old => ({
      ...old,
      bookings: dateRangeBookings,
      userBookings: allUserBookings
    }))
  }

  return {
    userState,
    handleFromDayChange,
    setNewAmountValue,
    refreshBookings
  }
}
